import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import CustomerFormModal from '../components/CustomerFormModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import './CustomerList.css';
import { toast } from '../components/Toast';

function CustomerList() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (!sortConfig || sortConfig.key !== key) return ' ↕';
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const sortedCustomers = React.useMemo(() => {
    let sortable = [...filteredCustomers];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        if (sortConfig.key === 'createdAt') {
          aVal = new Date(a.createdAt).getTime();
          bVal = new Date(b.createdAt).getTime();
        }

        if (aVal === undefined || aVal === null) aVal = '';
        if (bVal === undefined || bVal === null) bVal = '';

        if (typeof aVal === 'string') {
          return sortConfig.direction === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        } else {
          return sortConfig.direction === 'asc'
            ? (aVal > bVal ? 1 : aVal < bVal ? -1 : 0)
            : (aVal < bVal ? 1 : aVal > bVal ? -1 : 0);
        }
      });
    }
    return sortable;
  }, [filteredCustomers, sortConfig]);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    filterCustomers();
  }, [customers, searchTerm]);

  const fetchCustomers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get('/customers');
      const customerData = response.data.data?.content || response.data.content || response.data;
      setCustomers(customerData);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  const filterCustomers = () => {
    let filtered = [...customers];

    if (searchTerm) {
      filtered = filtered.filter(customer =>
        customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.phone.includes(searchTerm) ||
        (customer.email && customer.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        customer.id.toString().includes(searchTerm)
      );
    }

    setFilteredCustomers(filtered);
    setCurrentPage(0);
  };

  const handleCreate = () => {
    setSelectedCustomer(null);
    setShowModal(true);
  };

  const handleEdit = (customer) => {
    setSelectedCustomer(customer);
    setShowModal(true);
  };

  const handleView = (customer) => {
    navigate(`/customers/${customer.id}`);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedCustomer(null);
  };

  const handleModalSuccess = () => {
    setShowModal(false);
    setSelectedCustomer(null);
    fetchCustomers();
    showSuccessMessage('Customer saved successfully');
  };

  const handleDelete = (customer) => {
    setCustomerToDelete(customer);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!customerToDelete) return;
    try {
      await axios.delete(`/customers/${customerToDelete.id}`);
      setShowConfirmModal(false);
      setCustomerToDelete(null);
      fetchCustomers();
      showSuccessMessage('Customer deleted successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete customer');
      setShowConfirmModal(false);
      setCustomerToDelete(null);
    }
  };

  const showSuccessMessage = (message) => {
    toast.success(message);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  return (
    <div className="customer-list-container">
      <div className="page-header">
        <h1>Customer Management</h1>
        <button className="btn btn-primary" onClick={handleCreate}>
          Create Customer
        </button>
      </div>

      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by ID, name, phone, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button className="btn btn-secondary btn-sm" onClick={clearSearch}>
              Clear
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading customers...</div>
      ) : (
        <div className="table-container">
          <table className="customer-table">
            <thead>
              <tr>
                <th onClick={() => requestSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>Name {getSortIcon('name')}</th>
                <th onClick={() => requestSort('phone')} style={{ cursor: 'pointer', userSelect: 'none' }}>Phone {getSortIcon('phone')}</th>
                <th onClick={() => requestSort('email')} style={{ cursor: 'pointer', userSelect: 'none' }}>Email {getSortIcon('email')}</th>
                <th onClick={() => requestSort('address')} style={{ cursor: 'pointer', userSelect: 'none' }}>Address {getSortIcon('address')}</th>
                <th onClick={() => requestSort('createdAt')} style={{ cursor: 'pointer', userSelect: 'none' }}>Created At {getSortIcon('createdAt')}</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedCustomers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="no-data">No customers found</td>
                </tr>
              ) : (
                sortedCustomers.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage).map((customer) => (
                  <tr key={customer.id}>
                    <td data-label="Name"><strong>{customer.name}</strong></td>
                    <td data-label="Phone">{customer.phone}</td>
                    <td data-label="Email">{customer.email || '-'}</td>
                    <td data-label="Address">{customer.address || '-'}</td>
                    <td data-label="Created At">{new Date(customer.createdAt).toLocaleDateString()}</td>
                    <td data-label="Actions">
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleView(customer)}
                        >
                          View
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleEdit(customer)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(customer)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          
          {Math.ceil(filteredCustomers.length / itemsPerPage) > 1 && (
            <div className="pagination" style={{ padding: '16px 20px', borderTop: '1px solid var(--border)' }}>
              <button
                className="btn btn-sm"
                onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                disabled={currentPage === 0}
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {currentPage + 1} of {Math.ceil(filteredCustomers.length / itemsPerPage)} ({filteredCustomers.length} total)
              </span>
              <button
                className="btn btn-sm"
                onClick={() => setCurrentPage(prev => Math.min(Math.ceil(filteredCustomers.length / itemsPerPage) - 1, prev + 1))}
                disabled={currentPage >= Math.ceil(filteredCustomers.length / itemsPerPage) - 1}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <CustomerFormModal
          customer={selectedCustomer}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      <DeleteConfirmModal
        isOpen={showConfirmModal}
        title="Delete Customer"
        message={<>Are you sure you want to delete customer <strong>{customerToDelete?.name}</strong>?</>}
        onCancel={() => { setShowConfirmModal(false); setCustomerToDelete(null); }}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}

export default CustomerList;
